import { useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { useForm, useFieldArray } from 'react-hook-form';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import { Skeleton } from '@mui/material';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CardHeader from '@mui/material/CardHeader';
import LoadingButton from '@mui/lab/LoadingButton';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';

import { useTranslate } from 'src/locales';
import { useSetCandidateProfileMutation } from 'src/services/candidates/candidates.service';
import { generateCandidateProfileErrorMessage } from 'src/services/candidates/candidates.utils';
import { CandidateProfile, LanguageProficiency, LANGUAGE_PROFICIENCIES } from 'src/services/candidates/candidates.types';

import Iconify from 'src/components/iconify';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';

// ----------------------------------------------------------------------

type Props = {
  profile?: CandidateProfile;
  isLoading: boolean;
  mode?: 'edit' | 'view';
  onUpdate?: () => void;
};

type FormValues = {
  languages: { language: string; proficiency: LanguageProficiency | '' }[];
  memberships: { organisation: string; membership_level: string; since_year: string }[];
};

const THIS_YEAR = new Date().getFullYear();

/**
 * Languages and professional memberships (JT1-1887). They're shown with Skills, so they're visible
 * or hidden with that section; there's no separate visibility switch here.
 */
export default function ProfileLanguagesMemberships({ profile, isLoading, mode = 'edit', onUpdate }: Props) {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const [updateProfile] = useSetCandidateProfileMutation();

  const formMethods = useForm<FormValues>({ defaultValues: { languages: [], memberships: [] }, mode: 'all' });
  const { control, handleSubmit, reset, formState: { isSubmitting, isDirty } } = formMethods;
  const languages = useFieldArray({ control, name: 'languages' });
  const memberships = useFieldArray({ control, name: 'memberships' });

  const fromProfile = (): FormValues => ({
    languages: (profile?.languages ?? []).map((item) => ({ language: item.language, proficiency: item.proficiency ?? '' })),
    memberships: (profile?.memberships ?? []).map((item) => ({ organisation: item.organisation, membership_level: item.membership_level ?? '', since_year: item.since_year ? String(item.since_year) : '' })),
  });

  useEffect(() => {
    if (profile && !isLoading) reset(fromProfile(), { keepDirty: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, profile]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      await updateProfile({
        id: profile?.id as string,
        data: {
          languages: data.languages
            .filter((item) => item.language.trim())
            .map((item) => ({ language: item.language.trim(), proficiency: item.proficiency || null })),
          memberships: data.memberships
            .filter((item) => item.organisation.trim())
            .map((item) => ({
              organisation: item.organisation.trim(),
              membership_level: item.membership_level.trim() || null,
              since_year: item.since_year ? Number(item.since_year) : null,
            })),
        },
      }).unwrap();
      onUpdate?.();
      reset(data, { keepDirty: false });
    } catch (e) {
      enqueueSnackbar(generateCandidateProfileErrorMessage(e), { variant: 'error' });
      reset(fromProfile(), { keepDirty: false });
    }
  });

  const disabled = isLoading || mode === 'view';

  if (isLoading) {
    return (
      <Card>
        <CardHeader title={<Skeleton width={200} />} />
        <CardContent><Skeleton variant="rectangular" height={100} /></CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title={t('profile.sections.languages_memberships.title')} subheader={t('profile.sections.languages_memberships.subheader')} />
      <CardContent>
        <FormProvider methods={formMethods} onSubmit={onSubmit}>
          <Stack spacing={3}>
            <Stack spacing={1.5}>
              <Typography variant="subtitle2">{t('profile.sections.languages_memberships.languages')}</Typography>
              {!languages.fields.length && <Typography variant="body2" sx={{ color: 'text.secondary' }}>{t('profile.sections.languages_memberships.no_languages')}</Typography>}
              {languages.fields.map((field, index) => (
                <Stack key={field.id} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                  <RHFTextField name={`languages.${index}.language`} label={t('profile.sections.languages_memberships.language')} disabled={disabled} inputProps={{ maxLength: 100 }} />
                  <RHFSelect name={`languages.${index}.proficiency`} label={t('profile.sections.languages_memberships.proficiency')} disabled={disabled} sx={{ minWidth: 220 }}>
                    <MenuItem value="">{t('profile.sections.languages_memberships.proficiency_none')}</MenuItem>
                    {LANGUAGE_PROFICIENCIES.map((value) => (
                      <MenuItem key={value} value={value}>{t(`profile.sections.languages_memberships.proficiencies.${value}`)}</MenuItem>
                    ))}
                  </RHFSelect>
                  {!disabled && (
                    <IconButton aria-label={t('profile.sections.languages_memberships.remove_language')} onClick={() => languages.remove(index)}>
                      <Iconify icon="solar:trash-bin-trash-bold" />
                    </IconButton>
                  )}
                </Stack>
              ))}
              {!disabled && (
                <Button size="small" sx={{ alignSelf: 'flex-start' }} startIcon={<Iconify icon="mingcute:add-line" />} onClick={() => languages.append({ language: '', proficiency: '' })}>
                  {t('profile.sections.languages_memberships.add_language')}
                </Button>
              )}
            </Stack>

            <Stack spacing={1.5}>
              <Typography variant="subtitle2">{t('profile.sections.languages_memberships.memberships')}</Typography>
              {!memberships.fields.length && <Typography variant="body2" sx={{ color: 'text.secondary' }}>{t('profile.sections.languages_memberships.no_memberships')}</Typography>}
              {memberships.fields.map((field, index) => (
                <Stack key={field.id} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                  <RHFTextField name={`memberships.${index}.organisation`} label={t('profile.sections.languages_memberships.organisation')} disabled={disabled} inputProps={{ maxLength: 200 }} />
                  <RHFTextField name={`memberships.${index}.membership_level`} label={t('profile.sections.languages_memberships.membership_level')} disabled={disabled} inputProps={{ maxLength: 120 }} />
                  <RHFTextField name={`memberships.${index}.since_year`} label={t('profile.sections.languages_memberships.since_year')} type="number" disabled={disabled} inputProps={{ min: 1900, max: THIS_YEAR }} sx={{ maxWidth: { sm: 140 } }} />
                  {!disabled && (
                    <IconButton aria-label={t('profile.sections.languages_memberships.remove_membership')} onClick={() => memberships.remove(index)}>
                      <Iconify icon="solar:trash-bin-trash-bold" />
                    </IconButton>
                  )}
                </Stack>
              ))}
              {!disabled && (
                <Button size="small" sx={{ alignSelf: 'flex-start' }} startIcon={<Iconify icon="mingcute:add-line" />} onClick={() => memberships.append({ organisation: '', membership_level: '', since_year: '' })}>
                  {t('profile.sections.languages_memberships.add_membership')}
                </Button>
              )}
            </Stack>
          </Stack>

          {mode === 'edit' && (
            <CardActions sx={{ px: 0, pt: 2, justifyContent: 'flex-end' }}>
              <LoadingButton variant="contained" color="success" type="submit" loading={isSubmitting} disabled={!isDirty}>
                {t('common.save')}
              </LoadingButton>
            </CardActions>
          )}
        </FormProvider>
      </CardContent>
    </Card>
  );
}
